import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES } from "../data/classes";
import { SceneBackdrop } from "../components/game/SceneBackdrop";
import { themeForFloor } from "../utils/themes";
import { generateSanctuaryPassive } from "../data/passives";
import { generateChestContent, rollChestRarity } from "../data/chests";
import { getRandomEnemy } from "../data/enemies";
import { scaleEnemyStats, computeDamageWithFormula, applyChestBonuses } from "../utils/gameLogic";
import { Loader2, Crown, Heart, Zap, Swords, Package, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { SFX } from "../utils/audio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mini-parcours PvP : 4 salles ALÉATOIRES par joueur (indépendantes, non partagées).
// Chacun avance à son rythme ; la progression de l'adversaire est visible en TEMPS RÉEL (barre).
// À la fin, les deux joueurs s'affrontent en duel (PvPDuel).
const TOTAL_STEPS = 4;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Génère un parcours aléatoire (par joueur) : 1 sanctuaire, 1 coffre, 2 combats — ordre mélangé.
const generateRooms = () => shuffle(["sanctuary", "chest", "combat", "combat"]);

const PvPDungeon = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const { profile } = useProfile();

  const [match, setMatch] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | room | finishing | waiting | invalid
  const [character, setCharacter] = useState(null);
  const [rooms] = useState(generateRooms);
  const [step, setStep] = useState(0); // index into rooms (0..TOTAL_STEPS-1)
  const [sanctuaryOptions, setSanctuaryOptions] = useState([]);
  const [chestReward, setChestReward] = useState(null);
  const [enemy, setEnemy] = useState(null);
  const [enemyHp, setEnemyHp] = useState(0);
  const [log, setLog] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);
  const finishedRef = useRef(false);
  const characterRef = useRef(null);
  useEffect(() => { characterRef.current = character; }, [character]);

  const stopPoll = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };

  const fetchMatch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pvp/match/${matchId}`, { withCredentials: true });
      setMatch(res.data);
      if (res.data.status === "in_duel" || res.data.status === "finished") {
        stopPoll();
        navigate(`/pvp-duel/${matchId}`);
      }
      return res.data;
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 403) {
        setPhase("invalid");
        toast.error("Match introuvable");
      }
      return null;
    }
  }, [matchId, navigate]);

  const reportProgress = useCallback(async (floor) => {
    try {
      await axios.post(`${API}/pvp/match/${matchId}/dungeon-progress`, {
        floor, character: characterRef.current || {},
      }, { withCredentials: true });
    } catch (e) { /* ignore */ }
  }, [matchId]);

  const tier = Math.max(1, Math.floor((profile?.rank_points || 0) / 200) + 1);

  // Setup the room at index `idx`
  const setupRoom = useCallback((idx) => {
    const type = rooms[idx];
    if (type === "sanctuary") {
      setSanctuaryOptions([generateSanctuaryPassive("B"), generateSanctuaryPassive("B"), generateSanctuaryPassive("B")]);
    } else if (type === "chest") {
      setChestReward(generateChestContent(rollChestRarity(tier)));
    } else if (type === "combat") {
      const isElite = Math.random() < 0.3;
      const def = getRandomEnemy(isElite);
      const stats = scaleEnemyStats(def.baseStats, isElite ? tier + 1 : tier);
      setEnemy({ ...def, stats, isElite });
      setEnemyHp(stats.pv);
      setLog([`Un ${def.nom}${isElite ? " (élite)" : ""} surgit !`]);
    }
    setPhase("room");
  }, [rooms, tier]);

  // ----- Initial load -----
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const m = await fetchMatch();
      if (!m || cancelled) return;
      if (m.status !== "dungeon") {
        if (m.status === "in_duel" || m.status === "finished" || m.status === "waiting_ready") navigate(`/pvp-duel/${matchId}`);
        return;
      }
      if (!m.you.classe_id) {
        toast.error("État incohérent : reviens à la file");
        navigate("/pvp");
        return;
      }
      const cls = CLASSES[m.you.classe_id];
      const baseStats = { ...cls.baseStats, pvMax: cls.baseStats.pv };
      setCharacter({ classeId: m.you.classe_id, nom: cls.nom, stats: { ...baseStats }, passifs: [], gold: 0, energy: 3, energyMax: 4, floor: 1 });
      characterRef.current = { classeId: m.you.classe_id, stats: { ...baseStats }, passifs: [] };
      setStep(0);
      setupRoom(0);
      reportProgress(1);
      pollRef.current = setInterval(fetchMatch, 2000);
    })();
    return () => { cancelled = true; stopPoll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ----- Advance to next room (or finish) -----
  const completeRoom = useCallback(() => {
    const next = step + 1;
    if (next >= TOTAL_STEPS) {
      finishDungeon();
      return;
    }
    setStep(next);
    reportProgress(next + 1);
    setEnemy(null); setEnemyHp(0); setSanctuaryOptions([]); setChestReward(null); setLog([]);
    setupRoom(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, setupRoom, reportProgress]);

  // ----- Sanctuary pick -----
  const pickPassive = (passive) => {
    SFX.click?.();
    setCharacter((c) => {
      const stats = { ...c.stats };
      if (passive.stat && passive.value && passive.stat !== "heal") {
        stats[passive.stat] = (stats[passive.stat] || 0) + passive.value;
        if (passive.stat === "pv") stats.pvMax = (stats.pvMax || stats.pv) + passive.value;
      }
      return { ...c, stats, passifs: [...c.passifs, passive] };
    });
    completeRoom();
  };

  // ----- Chest collect -----
  const collectChest = () => {
    SFX.chest?.();
    setCharacter((c) => {
      let u = applyChestBonuses(c, chestReward.stats);
      u.gold = (u.gold || 0) + (chestReward.gold || 0);
      return u;
    });
    toast.success(`Coffre : +${chestReward.gold || 0} or`);
    completeRoom();
  };

  // ----- Combat -----
  const castSkill = (comp) => {
    if (submitting || !enemy || enemyHp <= 0) return;
    if (character.energy < comp.cout) { toast.error("Pas assez d'énergie"); return; }
    setSubmitting(true);
    const result = computeDamageWithFormula(character.stats, enemy.stats, {
      formula: comp.formula || ["atk"], multiplier: comp.multiplier || 1,
      atkElement: CLASSES[character.classeId]?.element, defElement: enemy.element,
    });
    const dmg = result.hit ? result.damage : 0;
    const newEnemyHp = Math.max(0, enemyHp - dmg);
    setEnemyHp(newEnemyHp);
    setLog((l) => [...l, `${comp.icon} ${comp.nom} : -${dmg} PV.${result.crit ? " (CRIT)" : ""}`].slice(-8));
    SFX.hit?.();
    setCharacter((c) => ({ ...c, energy: Math.max(0, c.energy - comp.cout) + 1 }));

    if (newEnemyHp <= 0) {
      setLog((l) => [...l, `${enemy.nom} vaincu ! ⚔️`].slice(-8));
      setSubmitting(false);
      setTimeout(() => completeRoom(), 700);
      return;
    }
    setTimeout(() => {
      const eres = computeDamageWithFormula(enemy.stats, character.stats, { atkElement: enemy.element, defElement: CLASSES[character.classeId]?.element });
      const ed = eres.hit ? eres.damage : 0;
      setCharacter((c) => ({ ...c, stats: { ...c.stats, pv: Math.max(1, c.stats.pv - ed) } }));
      setLog((l) => [...l, `${enemy.nom} riposte : -${ed} PV.`].slice(-8));
      setSubmitting(false);
    }, 450);
  };

  // ----- Finish dungeon -----
  const finishDungeon = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setPhase("finishing");
    try {
      const c = characterRef.current;
      const cls = CLASSES[c.classeId];
      const competences = (cls.competences || []).map((cp) => ({
        id: cp.id, nom: cp.nom, icon: cp.icon, cout: cp.cout, description: cp.description,
        degats: cp.degats || 5, effet: cp.effet || "",
      }));
      // Heal to full entering the duel — the passifs/stats collected are the carry.
      const finalChar = { ...c, stats: { ...c.stats, pv: c.stats.pvMax }, floor: TOTAL_STEPS };
      const res = await axios.post(`${API}/pvp/match/${matchId}/dungeon-finish`, { character: finalChar, competences }, { withCredentials: true });
      setMatch(res.data);
      if (res.data.status === "in_duel") {
        stopPoll();
        setTimeout(() => navigate(`/pvp-duel/${matchId}`), 800);
      } else {
        setPhase("waiting");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      finishedRef.current = false;
    }
  }, [matchId, navigate]);

  if (!user) {
    return (
      <div className="h-screen w-screen bg-slate-900 flex items-center justify-center text-white text-center p-4">
        <div>
          <p className="mb-3">Connecte-toi pour rejoindre le parcours PvP.</p>
          <button onClick={loginWithGoogle} className="bg-white text-slate-950 px-3 py-2 rounded-lg font-bold">Connexion</button>
        </div>
      </div>
    );
  }
  if (phase === "loading" || !match) {
    return <div className="h-screen w-screen bg-slate-900 flex items-center justify-center text-white"><Loader2 className="w-12 h-12 animate-spin" /></div>;
  }

  const theme = themeForFloor(1);
  const oppFinished = match.opponent.dungeon_finished;
  const oppFloor = match.opponent.dungeon_floor || 1;
  const oppPct = oppFinished ? 100 : Math.max(0, Math.min(100, Math.round(((oppFloor - 1) / TOTAL_STEPS) * 100)));
  const myPct = phase === "finishing" || phase === "waiting" ? 100 : Math.round((step / TOTAL_STEPS) * 100);
  const roomType = rooms[step];

  return (
    <div className="h-screen w-screen flex flex-col relative overflow-hidden">
      <SceneBackdrop sceneClass={theme.scene} particle={theme.particle} count={theme.particles} />
      <div className="relative z-10 flex flex-col h-full">
        {/* Top progress strip */}
        <div className="bg-slate-950/85 border-b-4 border-slate-950 px-3 py-2">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="font-display font-bold text-white text-sm flex items-center gap-1"><Crown className="w-4 h-4 text-yellow-400" /> Parcours aléatoire</span>
            <span className="text-xs text-slate-400">Salle {Math.min(step + 1, TOTAL_STEPS)}/{TOTAL_STEPS}</span>
          </div>
          {/* My bar */}
          <ProgressRow data-testid="my-progress" label="Toi" pct={myPct} color="from-emerald-500 to-teal-400" picture={match.you.picture} name="Toi" />
          {/* Opponent bar (real-time) */}
          <div data-testid="opponent-progress" className="mt-1">
            <ProgressRow label={match.opponent.name} pct={oppPct} color="from-rose-500 to-orange-400" picture={match.opponent.picture} name={match.opponent.name} finished={oppFinished} />
          </div>
        </div>

        {/* Sanctuary */}
        {phase === "room" && roomType === "sanctuary" && (
          <div data-testid="pvp-sanctuary" className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="max-w-3xl mx-auto">
              <h1 className="font-display text-2xl font-bold text-center text-purple-300 mb-2 flex items-center justify-center gap-2"><Sparkles className="w-6 h-6" /> Sanctuaire</h1>
              <p className="text-slate-300 text-center mb-5">Choisis UN passif pour renforcer ton personnage.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {sanctuaryOptions.map((p, i) => (
                  <button key={i} data-testid={`pick-passive-${i}`} onClick={() => pickPassive(p)}
                    className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-4 cartoon-shadow-lg-static text-left hover:-translate-y-1 transition-transform">
                    <p className="text-3xl text-center mb-2">{p.icon || "✨"}</p>
                    <p className="font-display font-bold text-base text-purple-300 mb-1">{p.nom}</p>
                    <p className="text-xs text-slate-300">{p.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Chest */}
        {phase === "room" && roomType === "chest" && chestReward && (
          <div data-testid="pvp-chest" className="flex-1 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-amber-400 to-yellow-500 border-4 border-slate-950 rounded-3xl p-6 max-w-md w-full text-center cartoon-shadow-lg-static">
              <Package className="w-12 h-12 text-slate-950 mx-auto mb-2" />
              <h2 className="font-display text-2xl font-bold text-slate-950 mb-2">Coffre trouvé !</h2>
              <p className="text-slate-900 font-bold mb-1">+{chestReward.gold || 0} or</p>
              <div className="text-slate-900 text-sm mb-3">{Object.entries(chestReward.stats || {}).map(([k, v]) => `+${v} ${k.toUpperCase()}`).join(" · ") || "Trésor"}</div>
              <button data-testid="pvp-chest-collect" onClick={collectChest} className="bg-slate-950 text-yellow-300 font-display font-bold px-4 py-2 rounded-xl border-4 border-slate-950">Récupérer & continuer</button>
            </div>
          </div>
        )}

        {/* Combat */}
        {phase === "room" && roomType === "combat" && character && enemy && (
          <div data-testid="pvp-combat" className="flex-1 overflow-y-auto p-3 flex flex-col">
            <div className="max-w-4xl w-full mx-auto flex flex-col flex-1">
              <h2 className="font-display text-xl font-bold text-center text-rose-400 mb-3 flex items-center justify-center gap-2"><Swords className="w-5 h-5" /> {enemy.isElite ? "Combat d'élite" : "Combat"}</h2>
              <div className="bg-slate-800/80 border-4 border-slate-950 rounded-2xl p-3 mb-3 text-center cartoon-shadow-lg-static">
                <div className="text-4xl mb-1">{enemy.emoji || "👹"}</div>
                <p className="font-display font-bold text-lg text-white">{enemy.nom}</p>
                <div className="mt-2 mx-auto max-w-xs">
                  <div className="h-4 bg-slate-950 border-2 border-slate-950 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-rose-500 to-orange-400 transition-all" style={{ width: `${(enemyHp / enemy.stats.pv) * 100}%` }} />
                  </div>
                  <p className="text-xs font-bold text-rose-300 mt-1">{enemyHp} / {enemy.stats.pv} PV</p>
                </div>
              </div>
              <div className="bg-slate-900/80 border-4 border-slate-950 rounded-2xl p-3 mb-3 cartoon-shadow-lg-static">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${CLASSES[character.classeId].couleur} border-2 border-slate-950 overflow-hidden`}>
                      <img src={CLASSES[character.classeId].image} alt="" className="w-full h-full object-cover" />
                    </div>
                    <p className="font-display font-bold text-white text-sm">{character.nom}</p>
                  </div>
                  <div className="text-right text-xs">
                    <p className="text-emerald-400 font-bold flex items-center gap-1 justify-end"><Heart className="w-3 h-3" /> {character.stats.pv} / {character.stats.pvMax}</p>
                    <p className="text-sky-400 font-bold flex items-center gap-1 justify-end"><Zap className="w-3 h-3" /> {character.energy} / {character.energyMax}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                  {CLASSES[character.classeId].competences.filter((c) => c.type === "attaque").map((c) => {
                    const canCast = character.energy >= c.cout && enemyHp > 0 && !submitting;
                    return (
                      <button key={c.id} data-testid={`pvp-skill-${c.id}`} onClick={() => castSkill(c)} disabled={!canCast}
                        className={`bg-slate-800 border-4 border-slate-950 rounded-xl p-2 text-left cartoon-shadow ${canCast ? "hover:-translate-y-0.5" : "opacity-50 cursor-not-allowed"}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-lg">{c.icon}</span>
                          <span className="text-[10px] text-sky-400 font-bold">{c.cout}⚡</span>
                        </div>
                        <p className="text-xs font-bold text-white mt-1 leading-tight">{c.nom}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div data-testid="pvp-combat-log" className="bg-slate-950/80 border-2 border-slate-950 rounded-lg p-2 max-h-28 overflow-y-auto">
                {log.map((l, i) => <p key={i} className="text-[11px] text-slate-200">• {l}</p>)}
              </div>
            </div>
          </div>
        )}

        {(phase === "finishing" || phase === "waiting") && (
          <div data-testid="dungeon-waiting" className="flex-1 flex items-center justify-center p-6 text-center">
            <div>
              <Loader2 className="w-12 h-12 animate-spin text-yellow-400 mx-auto mb-3" />
              <h2 className="font-display text-xl font-bold text-white mb-1">Parcours terminé !</h2>
              <p className="text-slate-300 text-sm mb-2">{oppFinished ? "Préparation du duel…" : `En attente de ${match.opponent.name}…`}</p>
              <div className="bg-slate-800/80 border-2 border-slate-950 rounded-xl p-3 max-w-xs mx-auto">
                <p className="text-xs text-yellow-400 font-bold uppercase">Ton build</p>
                <p className="text-white font-bold mt-1">{character?.stats?.pvMax} PV · {character?.stats?.atk} ATK · {character?.stats?.def} DEF</p>
                <p className="text-fuchsia-400 text-xs mt-1">{(character?.passifs || []).length} passif(s)</p>
              </div>
            </div>
          </div>
        )}

        {phase === "invalid" && (
          <div className="flex-1 flex items-center justify-center text-white">
            <button onClick={() => navigate("/pvp")} className="bg-rose-500 px-4 py-2 rounded-xl border-2 border-slate-950 font-bold">Retour</button>
          </div>
        )}
      </div>
    </div>
  );
};

const ProgressRow = ({ label, pct, color, picture, name, finished, ...rest }) => (
  <div className="flex items-center gap-2" {...rest}>
    {picture && <img src={picture} alt="" className="w-5 h-5 rounded-full border border-slate-700" />}
    <span className="text-xs font-bold text-slate-200 w-20 truncate">{name || label}</span>
    <div className="flex-1 h-3 bg-slate-800 border-2 border-slate-950 rounded-full overflow-hidden">
      <div className={`h-full bg-gradient-to-r ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
    <span className={`text-[11px] font-bold w-16 text-right ${finished ? "text-emerald-400" : "text-slate-300"}`}>{finished ? "✓ Prêt" : `${pct}%`}</span>
  </div>
);

export default PvPDungeon;
